import { Trans } from "@lingui/macro";
import { motion } from "framer-motion";
import Image, { StaticImageData } from "next/image";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Swiper from "swiper";
import { SwiperContainer, register } from "swiper/element";
import Navigation from "~/components/Carousel/Navigation";
import { Stack } from "~/guidelines/Flex";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import { heading5 } from "~/guidelines/Typography";
import Arrows from "../_assets/our-projects/arrows.svg";

interface ProjectData {
  id: string;
  label: string;
  beforeSrc: StaticImageData | string;
  afterSrc: StaticImageData | string;
}

const Wrapper = styled.div`
  width: 100%;
  height: auto;
`;

const SliderContainer = styled.div`
  width: 100%;
  position: relative;
  height: auto;
  overflow: hidden;
  border-radius: 32px;
  border: 4px solid black;

  > swiper-container {
    height: auto;
  }

  ${mediaQueries.lg()} {
    border: none;
  }
`;

const Slide = styled(Stack)`
  position: relative;
  width: 100%;
  height: 100%;
  align-items: start;
  overflow: hidden;
`;

const ImageComparerWrapper = styled.div`
  border-bottom: 3px solid black;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  overflow: hidden;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
`;

const MobileImageComparerWrapper = styled(ImageComparerWrapper)`
  min-width: auto;
  width: 100%;
  min-height: 57vh;
`;

const ImageWrapperBase = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  img {
    object-fit: cover;
    object-position: center;
  }
`;

const BeforeImageWrapper = styled(ImageWrapperBase)``;
const AfterImageWrapper = styled(ImageWrapperBase)``;

const SliderInput = styled.input.attrs({ type: "range" })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  cursor: ew-resize;
  opacity: 0;
  z-index: 20;
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 40px;
    height: 100%;
    cursor: ew-resize;
  }
  &::-moz-range-thumb {
    width: 40px;
    height: 100%;
    border: none;
    background: transparent;
    cursor: ew-resize;
  }
`;

const SliderVisuals = styled.div`
  background-color: black;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  pointer-events: none;
  z-index: 15;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SliderHandle = styled.div`
  width: 54px;
  height: 54px;
  min-width: 54px;
  min-height: 54px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.color.background.darkTertiary};
  border: 3px solid black;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
`;

const InfoTag = styled.span`
  position: absolute;
  top: 20px;
  display: inline-flex;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 60px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(12.6px);
  color: #fff;
  font-size: 11px;
  font-family: "Public Sans", sans-serif;
`;

const InfoTagLeft = styled(InfoTag)`
  left: 20px;
`;

const InfoTagRight = styled(InfoTag)`
  right: 20px;
`;

const MobileNavigation = styled(Stack)`
  padding: ${({ theme }) => theme.spacing(3, 3)};
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  background: ${({ theme }) => theme.color.background.lightFirst};

  ${mediaQueries.lg()} {
    display: none;
  }
`;

const StyledNavigation = styled(Navigation)`
  padding-top: 0;
`;

const Label = styled(motion.h5)`
  ${heading5}
  margin: 0;
  text-align: center;
`;

const ProjectSlide = ({ project }: { project: ProjectData }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const afterImageRef = useRef<HTMLDivElement>(null);
  const sliderVisualsRef = useRef<HTMLDivElement>(null);
  const comparerWrapperRef = useRef<HTMLDivElement>(null);
  const isTouching = useRef(false);

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    setSliderPosition(value);
  };

  // Touch event handlers
  const getRelativePosition = (clientX: number) => {
    const wrapper = comparerWrapperRef.current;
    if (!wrapper) return sliderPosition;
    const rect = wrapper.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(x, rect.width));
    return (x / rect.width) * 100;
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    isTouching.current = true;
    if (e.touches.length > 0) {
      const pos = getRelativePosition(e.touches[0].clientX);
      setSliderPosition(pos);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isTouching.current) return;
    if (e.touches.length > 0) {
      const pos = getRelativePosition(e.touches[0].clientX);
      setSliderPosition(pos);
    }
  };

  const handleTouchEnd = () => {
    isTouching.current = false;
  };

  useEffect(() => {
    if (afterImageRef.current)
      afterImageRef.current.style.clipPath = `inset(0 ${100 - sliderPosition}% 0 0)`;

    if (sliderVisualsRef.current)
      sliderVisualsRef.current.style.left = `${sliderPosition}%`;
  }, [sliderPosition]);

  useEffect(() => {
    setSliderPosition(50);
  }, [project]);

  return (
    <MobileImageComparerWrapper
      ref={comparerWrapperRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      style={{ touchAction: "none" }}
    >
      <AfterImageWrapper>
        <Image
          src={project.afterSrc}
          alt={`After renovation - ${project.label}`}
          fill
          priority={false}
        />

        <InfoTagRight>
          <Trans>After renovation</Trans>
        </InfoTagRight>
      </AfterImageWrapper>

      <BeforeImageWrapper ref={afterImageRef}>
        <Image
          src={project.beforeSrc}
          alt={`Before renovation - ${project.label}`}
          fill
          priority={false}
        />

        <InfoTagLeft>
          <Trans>Before renovation</Trans>
        </InfoTagLeft>
      </BeforeImageWrapper>

      <SliderVisuals ref={sliderVisualsRef}>
        <SliderHandle>
          <Image src={Arrows} style={{ margin: "auto" }} alt="arrows" />
        </SliderHandle>
      </SliderVisuals>

      <SliderInput
        min="0"
        max="100"
        value={sliderPosition}
        onChange={handleSliderChange}
        aria-label="Image comparison slider"
      />
    </MobileImageComparerWrapper>
  );
};

interface OurProjectsMobileProps {
  projects: ProjectData[];
}

const OurProjectsMobile: React.FC<OurProjectsMobileProps> = ({ projects }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const swiperRef = useRef<SwiperContainer>(null);

  useEffect(() => {
    register();

    if (swiperRef.current === null) {
      return;
    }
    swiperRef.current.initialize();

    const swiperInstance = swiperRef.current.swiper;
    if (swiperInstance) {
      swiperInstance.on("activeIndexChange", (swiper: Swiper) => {
        setActiveIndex(swiper.activeIndex);
      });
    }
  }, [swiperRef]);

  const currentProject = projects[activeIndex];

  return (
    <Wrapper>
      <SliderContainer>
        <swiper-container
          init={false}
          ref={swiperRef}
          slides-per-view={1}
          navigation={false}
          pagination={false}
          allow-touch-move={false}
        >
          {projects.map((project) => (
            <swiper-slide key={project.id}>
              <Slide>
                <ProjectSlide project={project} />
              </Slide>
            </swiper-slide>
          ))}
        </swiper-container>

        {currentProject && (
          <MobileNavigation>
            <StyledNavigation
              swiperRef={swiperRef}
              activeIndex={activeIndex}
              length={projects.length}
            />
            <Label>{currentProject.label}</Label>
          </MobileNavigation>
        )}
      </SliderContainer>
    </Wrapper>
  );
};

export default OurProjectsMobile;
